/**
 * Platform-aware upgrade router — RevenueCat on native iOS, Stripe elsewhere.
 */

'use client'

import { isCapacitorIosApp } from '@/lib/auth/capacitorSignIn'
import {
  logUpgradeBlockedDuringNativeOAuth,
  shouldBlockUpgradeDuringNativeOAuth,
  UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE,
} from '@/lib/monetization/blockUpgradeDuringNativeOAuth'
import { detectNativeIosForUpgrade } from '@/lib/monetization/detectUpgradePlatform'
import {
  purchasePackLeaderViaRevenueCat,
  restorePurchasesViaRevenueCat,
  tryApplyPackLeaderTierFromRevenueCat,
} from '@/lib/monetization/revenueCat'
import {
  applyPackLeaderTierLocally,
  syncSubscriptionWithServerBestEffort,
} from '@/lib/monetization/refreshSubscriptionState'
import {
  SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE,
  SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE,
} from '@/lib/monetization/iapConstants'
import { startStripeCheckout } from '@/lib/monetization/stripeCheckout'
import { trackUpgradeCompleted } from '@/lib/subscription/analytics'
import { logUpgradeFlow } from '@/lib/monetization/upgradeFlowDebugLog'

export interface StartUpgradeFlowOptions {
  userId?: string
  feature?: string
  source?: string
  email?: string
  name?: string
  /** When set, skips re-detection and forces RevenueCat (true) or Stripe (false) routing. */
  isNativeIosUpgrade?: boolean
}

export type StartUpgradeFlowResult =
  | { success: true; provider: 'revenuecat' | 'stripe'; redirecting?: boolean }
  | { success: false; cancelled?: boolean; error: string }

export function resolveNativeIosUpgradeRouting(
  isNativeIosUpgradeOption?: boolean
): boolean {
  if (isNativeIosUpgradeOption === true) return true
  if (isNativeIosUpgradeOption === false) return false
  return detectNativeIosForUpgrade() || isCapacitorIosApp()
}

async function runRevenueCatUpgradeFlow(
  options: StartUpgradeFlowOptions
): Promise<StartUpgradeFlowResult> {
  logUpgradeFlow('native shell confirmed', { feature: options.feature })
  logUpgradeFlow('native iOS path', { feature: options.feature })

  const purchase = await purchasePackLeaderViaRevenueCat()
  if (!purchase.ok) {
    if (purchase.cancelled) {
      console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', {
        path: 'revenuecat_cancelled',
      })
      return { success: false, cancelled: true, error: purchase.message }
    }
    if (
      purchase.message === SUBSCRIPTION_STATUS_UPDATING_USER_MESSAGE ||
      purchase.message === SUBSCRIPTION_PURCHASE_COMPLETED_REFRESHING_MESSAGE
    ) {
      const recovery = await tryApplyPackLeaderTierFromRevenueCat(
        'startUpgradeFlow_purchase_recovery'
      )
      if (recovery.applied) {
        void syncSubscriptionWithServerBestEffort()
        if (options.userId) {
          trackUpgradeCompleted(options.userId, options.feature)
        }
        console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', {
          path: 'revenuecat_success_recovered_entitlement',
        })
        return { success: true, provider: 'revenuecat' }
      }
    }
    console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', {
      path: 'revenuecat_failed',
      error: purchase.message,
    })
    return { success: false, error: purchase.message }
  }

  applyPackLeaderTierLocally('startUpgradeFlow_purchase_success')
  void syncSubscriptionWithServerBestEffort()

  if (options.userId) {
    trackUpgradeCompleted(options.userId, options.feature)
  }

  console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', {
    path: 'revenuecat_success_client_entitlement',
  })
  return { success: true, provider: 'revenuecat' }
}

/** RevenueCat-only upgrade — never calls Stripe. */
export async function runNativeIosUpgradeFlow(
  options: StartUpgradeFlowOptions = {}
): Promise<StartUpgradeFlowResult> {
  console.log('[UpgradeTrace9419bd7] startUpgradeFlow.native_iap_only', {
    feature: options.feature,
    hasUserId: Boolean(options.userId),
  })
  return runRevenueCatUpgradeFlow(options)
}

export async function startUpgradeFlow(
  options: StartUpgradeFlowOptions = {}
): Promise<StartUpgradeFlowResult> {
  console.log('[UpgradeTrace9419bd7] startUpgradeFlow.enter', {
    feature: options.feature,
    source: options.source,
    hasUserId: Boolean(options.userId),
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    isNativeIosUpgradeOption: options.isNativeIosUpgrade,
  })

  if (shouldBlockUpgradeDuringNativeOAuth()) {
    logUpgradeBlockedDuringNativeOAuth('startUpgradeFlow')
    console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', {
      path: 'blocked_native_oauth',
    })
    return { success: false, error: UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE }
  }

  const isNativeIosUpgrade = resolveNativeIosUpgradeRouting(options.isNativeIosUpgrade)

  console.log('[UpgradeTrace9419bd7] startUpgradeFlow.routing', {
    isNativeIosUpgrade,
    fromOption: options.isNativeIosUpgrade,
    feature: options.feature,
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    isCapacitorIosApp: isCapacitorIosApp(),
  })

  logUpgradeFlow('platform detected', {
    detectNativeIosForUpgrade: isNativeIosUpgrade,
    fromOption: options.isNativeIosUpgrade,
    feature: options.feature,
  })

  if (isNativeIosUpgrade) {
    return runRevenueCatUpgradeFlow(options)
  }

  logUpgradeFlow('Stripe path', { feature: options.feature })
  console.log('[UpgradeTrace9419bd7] startUpgradeFlow.path', { path: 'stripe' })
  const stripe = await startStripeCheckout({
    userId: options.userId,
    feature: options.feature,
    email: options.email,
    name: options.name,
  })

  if (!stripe.ok) {
    console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', {
      path: 'stripe_failed',
      error: stripe.error,
    })
    return { success: false, error: stripe.error }
  }

  console.log('[UpgradeTrace9419bd7] startUpgradeFlow.exit', { path: 'stripe_redirecting' })
  return { success: true, provider: 'stripe', redirecting: true }
}

export type RestorePurchasesResult =
  | { success: true; restored: boolean }
  | { success: false; error: string }

export async function restorePurchasesFlow(): Promise<RestorePurchasesResult> {
  if (shouldBlockUpgradeDuringNativeOAuth()) {
    logUpgradeBlockedDuringNativeOAuth('restorePurchasesFlow')
    return { success: false, error: UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE }
  }

  if (!resolveNativeIosUpgradeRouting()) {
    return { success: false, error: 'Restore Purchases is only available in the iOS app.' }
  }

  const restore = await restorePurchasesViaRevenueCat()
  if (!restore.ok) {
    return { success: false, error: restore.message }
  }

  if (!restore.restored) {
    return { success: true, restored: false }
  }

  applyPackLeaderTierLocally('restorePurchasesFlow_success')
  void syncSubscriptionWithServerBestEffort()

  return { success: true, restored: true }
}
